import { useState } from "react";
import {
  Aviso,
  Botao,
  Cabecalho,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Segmentado,
  Tabela,
} from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  useEnviosDePesquisa,
  useLinkDoEnvio,
  useReenviarConvite,
} from "../../nucleo/pesquisas";

const FILTROS = [
  { valor: "", rotulo: "Todos" },
  { valor: "pendente", rotulo: "Aguardando" },
  { valor: "respondida", rotulo: "Respondidos" },
  { valor: "expirada", rotulo: "Expirados" },
] as const;

const CHIP_DO_ESTADO = { pendente: "aguardando", respondida: "ok", expirada: "neutro" } as const;
const ROTULO_DO_ESTADO = { pendente: "Aguardando", respondida: "Respondida", expirada: "Expirada" } as const;

function dataHora(valor: string | null): string {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

/** Os convites: quem recebeu, quem respondeu, e o link para copiar quando o e-mail não chega. */
export function PainelDeEnvios() {
  const [estado, setEstado] = useState("");
  const envios = useEnviosDePesquisa(estado);
  const link = useLinkDoEnvio();
  const reenviar = useReenviarConvite();
  const [copiado, setCopiado] = useState<{ id: string; link: string } | null>(null);

  async function copiar(id: string) {
    const resposta = await link.mutateAsync(id);
    setCopiado({ id, link: resposta.link });
    try {
      await navigator.clipboard.writeText(resposta.link);
    } catch {
      /* sem permissão de área de transferência: o link continua na tela para copiar à mão */
    }
  }

  return (
    <div data-testid="painel-de-envios">
      <Segmentado rotulo="Situação do convite" itens={FILTROS} valor={estado} aoMudar={setEstado} className="mb-3" />
      {envios.isPending ? <p role="status">Carregando convites…</p> : null}
      {envios.isError ? <Aviso tom="erro">{mensagemDoConsole(envios.error)}</Aviso> : null}
      {envios.data && envios.data.length === 0 ? <Aviso tom="marca">Nenhum convite por aqui.</Aviso> : null}
      {link.isError || reenviar.isError ? (
        <Aviso tom="erro" className="mb-3">{mensagemDoConsole(link.error ?? reenviar.error)}</Aviso>
      ) : null}
      {copiado ? (
        <Aviso tom="sucesso" className="mb-3" data-testid="link-copiado">
          Link do convite: <code>{copiado.link}</code>
        </Aviso>
      ) : null}
      {reenviar.isSuccess ? (
        <Aviso tom="sucesso" className="mb-3" data-testid="convite-reenviado">
          Convite reenviado para {reenviar.data.email}.
        </Aviso>
      ) : null}
      {envios.data && envios.data.length > 0 ? (
        <Tabela legenda="Convites de pesquisa: destinatário, chamado, situação e nota.">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Pesquisa</CelulaCabecalho>
              <CelulaCabecalho>Empresa e chamado</CelulaCabecalho>
              <CelulaCabecalho>Destinatário</CelulaCabecalho>
              <CelulaCabecalho>Situação</CelulaCabecalho>
              <CelulaCabecalho>Nota</CelulaCabecalho>
              <CelulaCabecalho>Ação</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {envios.data.map((envio) => (
              <Linha key={envio.id} data-testid={`envio-${envio.id}`} data-estado={envio.estado}>
                <Celula>{envio.pesquisa_nome}</Celula>
                <Celula>
                  {envio.organizacao_nome}
                  <div className="celula-secundaria">
                    {envio.numero_do_chamado ? `Chamado #${envio.numero_do_chamado}` : "—"}
                  </div>
                </Celula>
                <Celula>{envio.email}</Celula>
                <Celula>
                  <Chip estado={CHIP_DO_ESTADO[envio.estado]}>{ROTULO_DO_ESTADO[envio.estado]}</Chip>
                  <div className="celula-secundaria">
                    {envio.respondido_em ? dataHora(envio.respondido_em) : `até ${dataHora(envio.expira_em)}`}
                  </div>
                </Celula>
                <Celula>
                  {envio.nota ?? "—"}
                  {envio.comentario ? <div className="celula-secundaria">{envio.comentario}</div> : null}
                </Celula>
                <Celula>
                  {envio.estado === "pendente" ? (
                    <div className="flex gap-2">
                      <Botao
                        tamanho="pequeno"
                        variante="fantasma"
                        aria-label={`Copiar o link do convite de ${envio.email}`}
                        disabled={link.isPending}
                        onClick={() => void copiar(envio.id)}
                      >
                        Copiar link
                      </Botao>
                      <Botao
                        tamanho="pequeno"
                        variante="fantasma"
                        aria-label={`Reenviar o convite para ${envio.email}`}
                        disabled={reenviar.isPending}
                        onClick={() => reenviar.mutate(envio.id)}
                      >
                        Reenviar
                      </Botao>
                    </div>
                  ) : null}
                </Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      ) : null}
    </div>
  );
}
